/* eslint-disable no-param-reassign */
/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import { IconButton } from "@dropbox/dig-components/buttons";
import {
  TextInput,
  TextInputRefObject,
} from "@dropbox/dig-components/text_fields";
import { Truncate } from "@dropbox/dig-components/truncate";
import { Text } from "@dropbox/dig-components/typography";
import { ThemeContainer, ThemeProvider } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  CheckmarkLine,
  CloseLine,
  DeleteLine,
  EditLine,
} from "@dropbox/dig-icons/assets";
import {
  $isAutoLinkNode,
  $isLinkNode,
  TOGGLE_LINK_COMMAND,
} from "@lexical/link";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $isAtNodeEnd } from "@lexical/selection";
import { $findMatchingParent, mergeRegister } from "@lexical/utils";
import { Link } from "@mirage/mosaics/BaseProfilePage/Link";
import { openURL } from "@mirage/service-platform-actions";
import cx from "classnames";
import {
  $getSelection,
  $isRangeSelection,
  BaseSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  ElementNode,
  KEY_ESCAPE_COMMAND,
  LexicalEditor,
  RangeSelection,
  SELECTION_CHANGE_COMMAND,
  TextNode,
} from "lexical";
import * as React from "react";
import { Dispatch, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import styles from "./FloatingLinkEditor.module.css";

const VERTICAL_GAP = 10;
const HORIZONTAL_OFFSET = 5;

const SUPPORTED_URL_PROTOCOLS = new Set([
  "http:",
  "https:",
  "mailto:",
  "sms:",
  "tel:",
]);

function sanitizeUrl(url: string): string {
  const correctedUrl = url.trim().replace(/^https:\/\/(https?:\/\/)/, "$1");
  try {
    const parsedUrl = new URL(correctedUrl);
    // eslint-disable-next-line no-script-url
    if (!SUPPORTED_URL_PROTOCOLS.has(parsedUrl.protocol)) {
      return "about:blank";
    }
  } catch {
    return correctedUrl;
  }
  return correctedUrl;
}

function setFloatingElemPositionForLinkEditor(
  targetRect: DOMRect | null,
  floatingElem: HTMLElement,
  anchorElem: HTMLElement,
  verticalGap: number = VERTICAL_GAP,
  horizontalOffset: number = HORIZONTAL_OFFSET,
): void {
  const scrollerElem = anchorElem.parentElement;

  if (targetRect === null || !scrollerElem) {
    floatingElem.style.opacity = "0";
    floatingElem.style.transform = "translate(-10000px, -10000px)";
    return;
  }

  const floatingElemRect = floatingElem.getBoundingClientRect();
  const anchorElementRect = anchorElem.getBoundingClientRect();
  const editorScrollerRect = scrollerElem.getBoundingClientRect();

  let top = targetRect.top - verticalGap;
  let left = targetRect.left - horizontalOffset;

  if (top < editorScrollerRect.top) {
    top += floatingElemRect.height + targetRect.height + verticalGap * 2;
  }

  if (left + floatingElemRect.width > editorScrollerRect.right) {
    left = editorScrollerRect.right - floatingElemRect.width - horizontalOffset;
  }

  top -= anchorElementRect.top;
  left -= anchorElementRect.left;

  floatingElem.style.opacity = "1";
  floatingElem.style.transform = `translate(${left}px, ${top}px)`;
}

function getSelectedNode(selection: RangeSelection): TextNode | ElementNode {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  } else {
    return $isAtNodeEnd(anchor) ? anchorNode : focusNode;
  }
}

const FloatingLinkEditor = ({
  editor,
  isLink,
  setIsLink,
  anchorElem,
  isLinkEditMode,
  setIsLinkEditMode,
}: {
  editor: LexicalEditor;
  isLink: boolean;
  setIsLink: Dispatch<boolean>;
  anchorElem: HTMLElement;
  isLinkEditMode: boolean;
  setIsLinkEditMode: Dispatch<boolean>;
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<TextInputRefObject>(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [editedLinkUrl, setEditedLinkUrl] = useState("https://");
  const [lastSelection, setLastSelection] = useState<BaseSelection | null>(
    null,
  );

  const updateLinkEditor = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const linkParent = $findMatchingParent(node, $isLinkNode);

      setLinkUrl(
        linkParent?.getURL() ?? ($isLinkNode(node) ? node.getURL() : ""),
      );
      if (isLinkEditMode) {
        inputRef.current?.focus();
        setEditedLinkUrl(linkUrl);
      }
    }
    const editorElem = editorRef.current;
    const nativeSelection = window.getSelection();

    if (editorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();

    if (
      selection !== null &&
      nativeSelection !== null &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode) &&
      editor.isEditable()
    ) {
      const domRect: DOMRect | undefined =
        nativeSelection.focusNode?.parentElement?.getBoundingClientRect();
      if (domRect) {
        domRect.y += 40;
        setFloatingElemPositionForLinkEditor(domRect, editorElem, anchorElem);
      }
      setLastSelection(selection);
    }

    return true;
  }, [anchorElem, editor, isLinkEditMode, linkUrl]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateLinkEditor();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateLinkEditor();
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        (e: React.KeyboardEvent) => {
          e.stopPropagation();
          if (isLink) {
            setIsLink(false);
            setIsLinkEditMode(false);
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH,
      ),
    );
  }, [editor, updateLinkEditor, setIsLink, isLink, setIsLinkEditMode]);

  useEffect(() => {
    if (isLinkEditMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLinkEditMode, isLink]);

  const monitorInputInteraction = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleLinkSubmission();
    } else if (event.key === "Escape") {
      if (!linkUrl || linkUrl === "https://") {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      }
      event.preventDefault();
      setIsLinkEditMode(false);
    }
  };

  const handleLinkSubmission = () => {
    if (lastSelection !== null) {
      if (linkUrl !== "") {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl(editedLinkUrl));
      }
      setEditedLinkUrl("https://");
      setIsLinkEditMode(false);
    }
  };

  return (
    <ThemeProvider mode="dark">
      <ThemeContainer>
        <div ref={editorRef} className={styles.editor}>
          {!isLink ? null : isLinkEditMode ? (
            <div className={styles.view}>
              <div className={styles.link}>
                <TextInput
                  ref={inputRef}
                  className={styles.input}
                  value={editedLinkUrl}
                  onChange={(event) => {
                    setEditedLinkUrl(event.target.value);
                  }}
                  onKeyDown={(event) => {
                    monitorInputInteraction(event);
                  }}
                />
              </div>

              <IconButton
                variant="borderless"
                tabIndex={0}
                onMouseDown={(event: React.MouseEvent) =>
                  event.preventDefault()
                }
                onClick={() => {
                  if (!linkUrl || linkUrl === "https://") {
                    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
                  }
                  setIsLinkEditMode(false);
                }}
              >
                <UIIcon src={CloseLine} />
              </IconButton>

              <IconButton
                variant="borderless"
                tabIndex={0}
                onMouseDown={(event: React.MouseEvent) =>
                  event.preventDefault()
                }
                onClick={handleLinkSubmission}
              >
                <UIIcon src={CheckmarkLine} />
              </IconButton>
            </div>
          ) : (
            <div className={styles.view}>
              <Link
                className={cx(styles.link, styles.text)}
                to={sanitizeUrl(linkUrl)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Text>
                  <Truncate maxWidth={285}>{linkUrl}</Truncate>
                </Text>
              </Link>

              <IconButton
                variant="borderless"
                tabIndex={0}
                onMouseDown={(event: React.MouseEvent) =>
                  event.preventDefault()
                }
                onClick={() => {
                  setEditedLinkUrl(linkUrl);
                  setIsLinkEditMode(true);
                }}
              >
                <UIIcon src={EditLine} />
              </IconButton>

              <IconButton
                variant="borderless"
                tabIndex={0}
                onMouseDown={(event: React.MouseEvent) =>
                  event.preventDefault()
                }
                onClick={() => {
                  editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
                }}
              >
                <UIIcon src={DeleteLine} />
              </IconButton>
            </div>
          )}
        </div>
      </ThemeContainer>
    </ThemeProvider>
  );
};

function isSelectionStartOrEnd(editor: LexicalEditor): boolean {
  const selection = $getSelection();

  if ($isRangeSelection(selection)) {
    const anchor = selection.anchor;
    const focus = selection.focus;

    if (anchor.key === focus.key && anchor.offset === focus.offset) {
      const node = editor.getElementByKey(anchor.key);
      if (node) {
        const textContent = node.textContent || "";
        return anchor.offset === 0 || anchor.offset === textContent.length;
      }
    }
  }

  return false;
}

function useFloatingLinkEditorToolbar(
  editor: LexicalEditor,
  anchorElem: HTMLElement,
) {
  const [activeEditor, setActiveEditor] = useState(editor);
  const [isLink, setIsLink] = useState(false);
  const [isLinkEditMode, setIsLinkEditMode] = useState(false);

  useEffect(() => {
    function updateToolbar() {
      const selection = $getSelection();
      if (isSelectionStartOrEnd(editor)) {
        setIsLink(false);
        setIsLinkEditMode(false);
      } else if ($isRangeSelection(selection)) {
        const node = getSelectedNode(selection);
        const linkParent = $findMatchingParent(node, $isLinkNode);
        const autoLinkParent = $findMatchingParent(node, $isAutoLinkNode);
        // We don't want this menu to open for auto links.
        if (linkParent !== null && autoLinkParent === null) {
          setIsLink(true);
          if (
            linkParent.getURL() === "https://" ||
            linkParent.getURL() === ""
          ) {
            setIsLinkEditMode(true);
          }
        } else {
          setIsLink(false);
          setIsLinkEditMode(false);
        }
      }
    }
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          updateToolbar();
          setActiveEditor(newEditor);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        CLICK_COMMAND,
        (payload) => {
          const selection = $getSelection();
          if (isSelectionStartOrEnd(editor)) {
            setIsLink(false);
            setIsLinkEditMode(false);
          } else if ($isRangeSelection(selection)) {
            const node = getSelectedNode(selection);
            const linkNode = $findMatchingParent(node, $isLinkNode);

            if ($isLinkNode(linkNode) && (payload.metaKey || payload.ctrlKey)) {
              openURL(linkNode.getURL());
              return true;
            }
          }
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor]);

  return createPortal(
    <FloatingLinkEditor
      editor={activeEditor}
      isLink={isLink}
      anchorElem={anchorElem}
      setIsLink={setIsLink}
      isLinkEditMode={isLinkEditMode}
      setIsLinkEditMode={setIsLinkEditMode}
    />,
    anchorElem,
  );
}

export function FloatingLinkEditorPlugin({
  anchorElem = document.body,
}: {
  anchorElem?: HTMLElement;
}) {
  const [editor] = useLexicalComposerContext();
  return useFloatingLinkEditorToolbar(editor, anchorElem);
}
